import React from 'react'
import { observable, action, makeObservable } from 'mobx'
import i18n from 'i18next'
import jsPDF from 'jspdf'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'
import {
    Line, Bar, Pie, Text, Gauge, Treemap, Radar, QuadrantAnalysis, MultiText,
} from 'frontend-charts'
import queryString from 'query-string'
import html2canvas from 'html2canvas'
import { startCase } from 'lodash'

import { removeStopwords } from 'stopword'
import MatrixChart from './charts/MatrixChart'
import DataFormatter from './dataFormatter'
import {
    sortBy, download, getProductMap,
} from '../../utils/helpers'
import { MIXPANEL_EVENTS, OID_TYPE_MAP } from '../../utils/MixpanelManager'
import QuadrantHeader from './QuadrantHeader'
import chartRenderSettings from './chartRenderSettings'
import Checkbox from '../form/Checkbox'
import RuleGroup from '../ReportV2/components/Modal/Filter/model/RuleGroup'
import Rule from '../ReportV2/components/Modal/Filter/model/Rule'
import TextResponseChart from './charts/TextResponseChart'
import { unescapeHtmlTags } from '../helpers'
import WordCloud from './charts/WordCloud'
import wordCloudMask from './charts/circleMask.svg'
import { getQuestionTitle, getOptionTitle } from '../../utils/QuestionHelper'

class DynamicDashboardStore {
    @observable editing = false

    @observable widgets = []

    defaultLayout = {
        lg: [],
        md: [],
        sm: [],
        xs: [],
        xxs: [],
    }

    @observable layouts = this.defaultLayout

    @observable dashboards = []

    @observable dashboard = {}

    @observable loading = true

    @observable loadingScopeData = true

    @observable useNewReport = true

    defaultGridProps = {
        rowHeight: 370,
    }

    responsiveCols = {
        lg: 8, md: 8, sm: 6, xs: 3, xxs: 3,
    }

    chartContainerStyles = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }

    gapSize = 10

    multiQuestionInfo = {
        benchmarksAllowed: ['column', 'bar'],
        respondentTypesAllowed: ['avg'],
        questions: {},
    }

    staticGridItems = false

    lastAllocatedRow

    industry

    surveyType

    productType

    respondentType

    oId

    urlSurveyDataIds = []

    productNames = {}

    portfolioNames = {}

    divisionNames = {}

    products = []

    portfolios = []

    divisions = []

    filterKpis = []

    defaultBenchmarkLabels = {
        industry: 'Industry',
        client: 'Company',
        division: 'Current Division',
        portfolio: 'Current Portfolio',
    }

    benchmarkLabels = {}

    possibleBenchmarks = {
        3: ['industry', 'client'],
        4: ['industry', 'client', 'division'],
        5: ['industry', 'client', 'division', 'portfolio'],
        6: ['industry', 'client', 'division', 'portfolio', 'sector'],
    }

    benchmarkPeriods = [
        {
            value: 'ROL36',
            label: '36 Months',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 36 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 36 },
        },
        {
            value: 'ROL84',
            label: 'Long-term',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 84 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 84 },
        },
    ]

    benchmarkTypeBackwardsCompat = { ROL: 'ROL-36', ALL: 'ROL-84' }

    @observable benchmarkPeriod = 'ROL36'

    @observable level = '' // This is for the temp maxbi level switch

    @observable years = new Map()

    @observable benchmarks = new Map()

    @observable widgetEditorKey = undefined

    @observable showCreateDashboard = false

    @observable scopeName = ''

    @observable currencyConversions

    @observable showTemplateCloneModal = false

    @observable showDuplicateModal = false

    @observable showManageDashboardsModal = false

    @observable showDashboardClone = false

    @observable showDashboardRename = false

    @observable showDashboardDelete = false

    @observable showDashboardExport = false

    @observable showTemplateSelection = false

    @observable showCreateTemplateModal = false

    @observable editingTemplate = false

    @observable confirmationModal = {
        show: false,
        onClose: () => { this.confirmationModal.show = false },
        onTrue: undefined,
        onFalse: undefined,
        title: undefined,
        description: undefined,
    }

    productMap

    todaysCurrencyRates

    benchmarkTags = []

    @observable showFilters = false

    @observable filters = {
        question: new RuleGroup(RuleGroup.getDefaultLogic('question')),
        campaign: new RuleGroup(RuleGroup.getDefaultLogic('campaign')),
        responseDate: new RuleGroup(RuleGroup.getDefaultLogic('responseDate')),
        openEnded: new RuleGroup(RuleGroup.getDefaultLogic('question')),
    }

    @observable filterRespondentType

    @observable showSelectFilterRespondent = false

    @observable filterStore = {
        reportPages: [],
        addFilterRule: (ruleGroup, label, id, rule, questionTypeCode) => {
            const logic = ['DT'].includes(questionTypeCode) ? 'AND' : 'OR'
            ruleGroup.addRule(new Rule(
                {
                    id,
                    label,
                    options: [rule],
                    logic,
                    questionTypeCode,
                },
            ), questionTypeCode === 'DT')
        },
        allowPinning: true,
        pinQuestion: (questionId, options, questionTypeCode, label, ruleGroup, isOpenEnded) => {
            if (this.filterStore.pinnedFilterQuestion === questionId) {
                this.filterStore.pinnedFilterOptions = []
                this.filterStore.pinnedFilterQuestion = ''
                this.filterStore.pinnedQuestionTypeCode = ''
                this.filterStore.pinnedQuestionLabel = ''
                this.filterStore.pinnedRuleGroup = undefined
                this.filterStore.pinnedIsOpenEnded = false
                return
            }
            this.filterStore.pinnedFilterOptions = options
            this.filterStore.pinnedFilterQuestion = questionId
            this.filterStore.pinnedQuestionTypeCode = questionTypeCode
            this.filterStore.pinnedQuestionLabel = label
            this.filterStore.pinnedRuleGroup = ruleGroup
            this.filterStore.pinnedIsOpenEnded = isOpenEnded
        },
        pinnedFilterQuestion: '',
        pinnedFilterOptions: [],
        pinnedQuestionTypeCode: '',
        pinnedQuestionLabel: '',
        pinnedIsOpenEnded: false,
        pinnedRuleGroup: undefined,
    }

    @observable showWidgetCreator = false

    @observable widgetEditorKeyNew = undefined

    @observable resizingElement = undefined

    @observable fixedFilterPins = []

    @observable showAddPinnedFilter = false

    @observable dashboardFilters = []

    @observable dashboardFilterRuleGroups = {}

    @observable showAddPinnedCustomTagFilter = false

    @observable pinnedCustomTagFilters = []

    @observable filterChangeKey = uuidv4()

    constructor(store) {
        makeObservable(this)
        this.appStore = store
        const maskImage = new Image()
        maskImage.src = wordCloudMask
        this.wordCloudMaskimage = maskImage
    }

    getProductSector = async () => {
        const { appStore } = this
        if (this.oId !== 6) return {}

        return appStore.http.get(`/product/${this.recordId}/sector`)
    }

    init = async (dontRefreshProducts, dontCallMixpanel = false) => {
        const { appStore } = this
        const { http, userData } = appStore
        const { location } = window
        const params = queryString.parse(location.search)
        this.loading = true
        this.setupTypes()
        this.benchmarks.clear()
        this.setBenchmarkOptions()
        this.possibleBenchmarks[this.oId].forEach((b) => {
            this.benchmarks.set(b, false)
        })

        this.benchmarkLabels = { ...this.defaultBenchmarkLabels }
        this.tagBenchmarkLabels = {}
        Reflect.deleteProperty(this, 'sectorId')

        if (!dontRefreshProducts) {
            const [
                products,
                portfolios,
                divisions,
                surveys,
                productMap,
                sector,
            ] = await Promise.all([
                this.getProducts(),
                this.getPortfolios(),
                this.getDivisions(),
                this.getSurveys(),
                getProductMap(http, userData.clientId),
                this.getProductSector(),
            ])

            const latestSurveys = Object.values(surveys
                .filter((survey) => (
                    survey.status !== 4
                        && [1, 2, 3, 6].includes(survey.productStatus)
                        && survey.isPrimary
                        && survey.surveyType !== 'GE'
                ))
                .reduce((map, survey) => {
                    const { respondentType, surveyId, year } = survey
                    map[respondentType] ||= {
                        year: 0,
                    }

                    if (year > map[respondentType].year) {
                        map[respondentType] = {
                            surveyId,
                            year,
                        }
                    }

                    return map
                }, {})).map(({ surveyId }) => surveyId)

            const [benchmarkTags, filterSurveys] = await Promise.all([
                this.getBenchmarkTags(latestSurveys),
                this.getFullSurveys(latestSurveys),
            ])

            await this.loadFullQuestions(filterSurveys)

            this.filterStore.locale = this.appStore.userData.locale
            this.filterStore.appStore = { survey: { metadata: { defaultLocale: this.appStore.userData.locale } } }

            this.filterSurveys = filterSurveys
            this.filterQuestions = this.getPageOptions()
            this.questionTypes = Object.assign({}, ...filterSurveys.map((s) => (
                s.survey.pages.map((p) => p.questions?.map((q) => ({ [q.id]: q.questionTypeCode })) || [{}])
            )).flat(3))
            await this.loadAllOpenEnded()

            this.tagBenchmarks = this.getTagBenchmarks(benchmarkTags)

            this.tagBenchmarks.forEach(({ value, label, shortLabel }) => {
                this.benchmarkLabels[value] = label
                this.benchmarks.set(value, false)
                this.tagBenchmarkLabels[value] = shortLabel
            })

            const { name, termId } = sector

            if (termId) {
                this.benchmarkLabels.sector = `Sector - (${name})`
                this.benchmarks.set('sector', false)
                this.sectorId = termId
            }

            this.productNames = products
            this.portfolioNames = portfolios
            this.divisionNames = divisions
            this.surveys = surveys
            this.productMap = productMap
            this.benchmarkTags = this.formatBenchmarkTags(benchmarkTags)
            const tagNames = benchmarkTags.reduce((tn, bt) => {
                bt.tagOptions.forEach((top) => {
                    const config = JSON.parse(top.tagOptionConfig)
                    tn = {
                        ...tn,
                        [top.tagOptionId]: R.pathOr(
                            R.pathOr('', ['langs', 'en_GB'], config), ['name'], config,
                        ),
                    }
                })
                return tn
            }, {})
            this.tagNames = tagNames

            this.dataFormatter = new DataFormatter(
                this.appStore,
                this.productNames,
                this.portfolioNames,
                this.divisionNames,
                this,
                tagNames,
            )
            this.loadingScopeData = true
            this.setScopeSelectorData()
            this.loadingScopeData = false
            this.setBreadcrumbs()
        }
        await this.getDashboards(undefined, false)
        const clientDashboards = this.dashboards.filter((d) => d.clientId === userData.clientId || d.value === 'new')
        const urlDash = clientDashboards.find((d) => d.dashboardId === Number(params.dashboardId))
        const levels = this.getLevels()
        this.resetYears()
        if (levels.size) {
            const levelsArr = Array.from(levels)
            const [l, d] = levelsArr.find((level) => R.pathOr(undefined, [1, 'dashboardId'], level) === Number(params.dashboardId)) || levelsArr[0]

            this.level = l
            this.dashboard = d
            this.dashboardFilters = dontRefreshProducts
                ? this.dashboardFilters
                : R.pathOr([], ['widgets', 'dashboardFilters'], d)
            this.pinnedCustomTagFilters = dontRefreshProducts
                ? this.pinnedCustomTagFilters
                : R.pathOr([], ['widgets', 'pinnedCustomTagFilters'], d)
            this.widgets = await this.getChartData(d.widgets.widgets)
            this.layouts = d.widgets.layouts
        } else if (clientDashboards.length > 1) {
            const [dashboard] = clientDashboards
            this.dashboard = urlDash || dashboard
            this.dashboardFilters = this.dashboardFilters.length
                ? this.dashboardFilters
                : R.pathOr([], ['widgets', 'dashboardFilters'], dashboard)
            this.pinnedCustomTagFilters = this.pinnedCustomTagFilters.length
                ? this.pinnedCustomTagFilters
                : R.pathOr([], ['widgets', 'pinnedCustomTagFilters'], dashboard)
            this.widgets = await this.getChartData(dashboard.widgets.widgets)
            this.layouts = dashboard.widgets.layouts
        }
        this.setUrlDashboardId()

        this.setInitialScopeName()
        this.lastAllocatedRow = this.getLastAllocatedRow()
        this.loading = false
        if (!dontCallMixpanel) this.trackDashboardLoaded()
    }

    trackDashboardLoaded = () => {
        const { appStore } = this
        const { pathname } = window.location

        const hasActiveFilters = this.dashboardFilters.some((f) => f.options?.length > 0)
            || this.pinnedCustomTagFilters.some((f) => f.options?.length > 0)

        const activeFilterCount = [
            ...this.dashboardFilters.filter((f) => f.options?.length > 0),
            ...this.pinnedCustomTagFilters.filter((f) => f.options?.length > 0),
        ].flat().length

        const accessContext = pathname.includes('/survey/') ? 'survey'
            : pathname.includes('/maxbi/') ? 'maxbi'
            : 'dashboard_hub'

        const surveyId = accessContext === 'survey'
            ? pathname.split('/').filter(Boolean)[1]
            : undefined

        appStore.mixpanel?.trackDashboardLoaded({
            dashboardVersion: 'v1',
            dashboardId: this.dashboard?.dashboardId,
            dashboardName: this.dashboard?.name,
            oId: this.oId,
            recordId: this.recordId,
            accessContext,
            surveyId,
            hasActiveFilters,
            activeFilterCount,
        })
    }

    trackPinnedFilterChanged = (filterType, filterLabel, selectedCount) => {
        const { appStore } = this
        appStore.mixpanel?.track(MIXPANEL_EVENTS.DASHBOARD_PINNED_FILTER_CHANGED, {
            dashboard_version: 'v1',
            filter_type: filterType,
            filter_label: filterLabel,
            filter_selected_count: selectedCount,
            dashboard_id: this.dashboard?.dashboardId,
            dashboard_name: this.dashboard?.name,
            dashboard_type: OID_TYPE_MAP[this.oId],
        })
    }

    trackFilterModal = (act, filterType) => {
        const { appStore } = this
        const eventMap = {
            opened: MIXPANEL_EVENTS.DASHBOARD_FILTER_OPENED,
            saved: MIXPANEL_EVENTS.DASHBOARD_FILTER_SAVED,
            closed: MIXPANEL_EVENTS.DASHBOARD_FILTER_CLOSED,
        }
        appStore.mixpanel?.track(eventMap[act], {
            dashboard_version: 'v1',
            filter_type: filterType,
            dashboard_id: this.dashboard?.dashboardId,
            dashboard_name: this.dashboard?.name,
            dashboard_type: OID_TYPE_MAP[this.oId],
        })
    }

    trackEditAction = (act, extras = {}) => {
        const { appStore } = this
        const eventMap = {
            started: MIXPANEL_EVENTS.DASHBOARD_EDIT_STARTED,
            saved: MIXPANEL_EVENTS.DASHBOARD_EDIT_SAVED,
            exited: MIXPANEL_EVENTS.DASHBOARD_EDIT_EXITED,
        }
        appStore.mixpanel?.track(eventMap[act], {
            dashboard_version: 'v1',
            dashboard_id: this.dashboard?.dashboardId,
            dashboard_name: this.dashboard?.name,
            dashboard_type: OID_TYPE_MAP[this.oId],
            ...extras,
        })
    }

    getTagBenchmarks = (benchmarkTags) => {
        const { langcode } = this.appStore
        return benchmarkTags.flatMap(({
            customTagName, customTagId, customTagConfig, tagOptions, parentConfig,
        }) => {
            const config = parentConfig || customTagConfig
            const scopes = JSON.parse(config).benchmarkOptions.filter((scope) => scope !== 'publish')

            return scopes.flatMap((scope) => (
                tagOptions.map(({ tagOptionId, tagOptionConfig }) => {
                    const parsedTagConfig = JSON.parse(tagOptionConfig)
                    const tagName = R.pathOr(
                        R.pathOr(
                            R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                            ['langs', langcode],
                            parsedTagConfig,
                        ),
                        ['name'], parsedTagConfig,
                    )
                    return {
                        tagOptionId,
                        customTag: customTagId,
                        customTagName,
                        scope,
                        shortLabel: tagName,
                        value: `${scope}_${tagOptionId}`,
                        label: `${scope} (${customTagName} - ${tagName})`,
                    }
                })
            ))
        })
    }

    formatBenchmarkTags = (benchmarkTags) => {
        const { langcode } = this.appStore
        return benchmarkTags.map((tag) => {
            const newTagOptions = tag.tagOptions.map((top) => {
                const parsedTagConfig = JSON.parse(top.tagOptionConfig)
                const tagName = R.pathOr(
                    R.pathOr(
                        R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                        ['langs', langcode],
                        parsedTagConfig,
                    ),
                    ['name'], parsedTagConfig,
                )
                return {
                    ...top,
                    tagOptionName: tagName,
                }
            })
            return { ...tag, tagOptions: newTagOptions }
        })
    }

    setUrlDashboardId = () => {
        const currentUrl = window.location.href
        const url = new URL(currentUrl)
        url.searchParams.set('dashboardId', this.dashboard.dashboardId)
        window.history.pushState({}, '', url)
    }

    updateConfirmationModal = (obj) => {
        const { confirmationModal } = this
        confirmationModal.show = obj.show
        confirmationModal.onClose = obj.onClose || (() => { this.confirmationModal.show = false })
        confirmationModal.onTrue = obj.onTrue
        confirmationModal.onFalse = obj.onFalse
        confirmationModal.title = obj.title || ''
        confirmationModal.description = obj.description || ''
    }

    setBenchmarkOptions = () => {
        const { appStore } = this
        const { userData } = appStore
        const benchmarks = userData.benchmarks
            ? Object.keys(userData.benchmarks).filter((b) => userData.benchmarks[b])
                .map((b) => (b === 'company' ? 'client' : b))
            : []
        this.possibleBenchmarks[this.oId] = this.possibleBenchmarks[this.oId]
            .filter((b) => benchmarks.includes(b))
    }

    setScopeName = (scopeName) => {
        this.scopeName = scopeName
    }

    setInitialScopeName = () => {
        const { location } = window
        const { appStore } = this
        const params = queryString.parse(location.search)
        if (params.divisionId) {
            this.scopeName = `${this.divisionNames[params.divisionId]}`
        } else if (params.portfolioId) {
            this.scopeName = `${this.portfolioNames[params.portfolioId]}`
        } else if (params.productId) {
            this.scopeName = `${this.productNames[params.productId]}`
        } else {
            this.scopeName = `${appStore.userData.clientName}`
        }
        this.setBreadcrumbs()
    }

    setupTypes = () => {
        const { location } = window
        const { appStore } = this
        const params = queryString.parse(location.search)
        this.oId = 3
        this.recordId = appStore.userData.clientId
        if (params.divisionId) {
            this.oId = 4
            this.recordId = Number(params.divisionId)
        }
        if (params.portfolioId) {
            this.oId = 5
            this.recordId = Number(params.portfolioId)
        }
        if (params.productId) {
            this.oId = 6
            this.recordId = Number(params.productId)
        }
        this.urlSurveyDataIds = params.surveyDataId ? params.surveyDataId.split(',') : []
    }

    getLevels = () => {
        const { dashboards, oId, appStore } = this
        const { userData } = appStore
        const levels = new Map()
        dashboards
            .slice()
            .sort((a, b) => {
                const aPriority = R.pathOr(-1, ['widgets', 'defaultDashSettings', 'priority'], a)
                const bPriority = R.pathOr(-1, ['widgets', 'defaultDashSettings', 'priority'], b)
                return aPriority - bPriority
            })
            .filter((d) => d.oId === oId && d.clientId === userData.clientId).forEach((d) => {
                const level = R.pathOr(undefined, ['widgets', 'defaultDashSettings', 'level'], d)
                if (level) levels.set(level, d)
            })
        return levels
    }

    changeLevel = (level, dashboard) => {
        const confirmationSettings = {
            show: this.editing,
            onFalse: () => { this.confirmationModal.show = false },
            title: i18n.t('dashbaords.unsavedChanges.title'),
            description: i18n.t('dashbaords.unsavedChanges.description'),
            onTrue: () => {
                this.trackEditAction('exited')
                this.editing = false
                this.level = level
                this.changeDashboard(dashboard)
                this.updateConfirmationModal({ show: false })
            },
        }
        if (this.editing) {
            this.updateConfirmationModal(confirmationSettings)
        } else {
            this.level = level
            this.changeDashboard(dashboard)
        }
    }

    getDashboards = async (dashId, refreshData = true) => {
        const { appStore } = this
        const { userData } = appStore
        const dashboards = await appStore.http.get('/dashboard')

        this.dashboards = dashboards
            .filter((d) => {
                if (this.oId === 6 && d.productType !== this.productType) return false
                if (this.oId !== 3) {
                    return d.oId === this.oId && (d.recordId === this.recordId || d.recordId === 0)
                }
                return d.oId === this.oId
            })
            .map((d) => ({ ...d, label: d.name, value: d.dashboardId }))
            .map((d) => {
                try {
                    return { ...d, widgets: JSON.parse(d.widgets) }
                } catch (e) {
                    return undefined
                }
            })
            .filter((d) => !!d)
            .sort((a, b) => R.pathOr(0, ['widgets', 'defaultDashSettings', 'priority'], a) - R.pathOr(0, ['widgets', 'defaultDashSettings', 'priority'], b))
            .concat([{ value: 'new', label: 'New' }])

        const clientDashboards = this.dashboards.filter((d) => d.clientId === userData.clientId || d.value === 'new')
        if (dashId) {
            const dashIndex = clientDashboards.findIndex((d) => d.dashboardId === dashId)
            this.changeDashboard(clientDashboards[dashIndex || 0], refreshData)
            return
        }
        this.changeDashboard(clientDashboards[0], refreshData)
    }

    getProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/product')
            this.products = products
            return products.reduce((obj, p) => {
                if (this.oId === 6 && p.productId === this.recordId) {
                    this.productType = p.productType
                }
                return { ...obj, [p.productId]: `${p.name}${p.edition ? ` - ${p.edition}` : ''}` }
            }, {})
        } catch (e) {
            return {}
        }
    }

    getPortfolios = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const portfolios = role > 130 && role !== 150 ? [] : await appStore.http.get('/portfolio')
            this.portfolios = portfolios
            return portfolios.reduce((obj, p) => ({ ...obj, [p.portfolioId]: p.name }), {})
        } catch (e) {
            this.portfolios = []
            return {}
        }
    }

    getDivisions = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const divisions = role > 120 && role !== 150 ? [] : await appStore.http.get('/division')
            this.divisions = divisions
            return divisions.reduce((obj, p) => ({ ...obj, [p.divisionId]: p.name }), {})
        } catch (e) {
            this.divisions = []
            return {}
        }
    }

    getBenchmarkTags = async () => {
        if (process.env.USE_CUSTOM_BENCHMARKS !== 'true') return []
        const response = await this.appStore.http.get('/benchmarkTags')
        return response.benchmarkTags
    }

    setScopeSelectorData = () => {
        const {
            divisions, portfolios, products, appStore,
        } = this
        const { userData } = appStore
        if ([140, 170].includes(userData.role)) {
            this.scopeSelectorData = products.map((p) => ({
                ...p,
                label: `${p.name}${p.edition ? ` - ${p.edition}` : ''}`,
                value: p.productId,
                scope: 'product',
            }))
            return
        }
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const portfolioObj = portfolios.reduce((po, p) => {
            po[p.portfolioId] = {
                ...p, label: p.name, value: p.portfolioId, scope: 'portfolio', children: [],
            }
            return po
        }, {})

        products.forEach((p) => portfolioObj[p.portfolioId].children.push(
            {
                ...p, label: `${p.name}${p.edition ? ` - ${p.edition}` : ''}`, value: p.productId, scope: 'product',
            },
        ))

        if (isMultiDivision && divisions && divisions.length) {
            const divisionObj = divisions.reduce((dObj, d) => {
                dObj[d.divisionId] = {
                    ...d, label: d.name, value: d.divisionId, scope: 'division', children: [],
                }
                return dObj
            }, {})

            Object.values(portfolioObj).forEach((p) => divisionObj[p.divisionId].children.push(p))
            this.scopeSelectorData = Object.values(divisionObj)
            return
        }
        this.scopeSelectorData = Object.values(portfolioObj)
    }

    getStandardSurveys = () => this.appStore.http.get('/survey')

    getMaxbiSurveys = () => this.appStore.http.get('/maxbi?active=1')

    getSurveys = async () => {
        if (this.oId !== 6) return []
        const [normal, maxbi] = await Promise.all(
            [this.getStandardSurveys(), this.getMaxbiSurveys()],
        )
        const surveys = [
            ...maxbi.map((s) => ({ ...s, surveyType: 'EM', industry: 'CE' })),
            ...normal,
        ].filter((s) => s.productId === this.recordId)

        return surveys
    }

    changeDashboard = async (dashboard, refreshData = true) => {
        this.loading = true
        const { appStore } = this
        this.dashboard = dashboard
        if (dashboard.value === 'new') {
            this.widgets = []
            this.layouts = this.defaultLayout
            this.dashboard.oId = this.oId
            this.dashboard.clientId = appStore.userData.clientId
            this.dashboard.isPublic = true
            this.dashboard.recordId = this.recordId
            this.dashboard.userId = appStore.userData.userId
            // ---------------------------------------------------------
            this.loading = false
            return
        }
        if (refreshData) {
            this.dashboardFilters = R.pathOr([], ['widgets', 'dashboardFilters'], dashboard)
            this.pinnedCustomTagFilters = R.pathOr([], ['widgets', 'pinnedCustomTagFilters'], dashboard)
            this.widgets = await this.getChartData(dashboard.widgets.widgets)
            this.layouts = dashboard.widgets.layouts
            this.loading = false
        }
        this.setUrlDashboardId()
    }

    setMultiQuestionMap = (chartIndex, q) => {
        const questionId = q.value
        const chartMultiQuestions = this.multiQuestionInfo.questions[chartIndex]
        if (!chartMultiQuestions) this.multiQuestionInfo.questions[chartIndex] = new Map()
        if (this.multiQuestionInfo.questions[chartIndex].get(questionId)) return
        this.multiQuestionInfo.questions[chartIndex].set(questionId, q.name)
    }

    getMultiQuestionInfo = (q) => {
        const questionId = R.pathOr(q.parentId, ['question', 'parentId'], q) || R.pathOr(R.pathOr(q.id, ['question', 'id'], q), ['questionId'], q)
        const formId = R.pathOr(q.formId, ['question', 'formId'], q)
        const isNps = R.pathOr(R.pathOr(false, ['isNps'], q), ['question', 'isNps'], q) ? 'nps' : 'avg'
        const responseType = (R.pathOr(q.parentId, ['question', 'parentId'], q) || formId)
            && !R.pathOr(R.pathOr(undefined, ['questionTypeCode'], q), ['question', 'questionTypeCode'], q) === 'TB - row'
            ? 'pct' : isNps
        return {
            questionId,
            formId,
            responseType: questionId && questionId !== '00000000-0000-0000-0000-000000000000' ? responseType : q.value,
        }
    }

    getBenchmarkData = async (charts) => {
        const { appStore } = this
        const benchmarks = this.possibleBenchmarks[this.oId]
        const body = {
            widget: [],
        }
        const currentPortfolioId = this.oId === 6
            ? this.products.find((p) => p.productId === this.recordId).portfolioId
            : undefined
        const currentDivisionId = this.oId >= 5
            ? R.pathOr(undefined, ['divisionId'], this.portfolios.find((p) => (p.portfolioId === (this.oId === 5 ? this.recordId : currentPortfolioId))))
            : undefined

        charts.forEach((c, chartIndex) => {
            benchmarks.forEach((b) => {
                this.benchmarkPeriods.forEach((period) => {
                    if ((b === 'division' && c.benchmarkType !== 'YOY' && this.oId === 4 && c.scope === 'division')
                    || (b === 'portfolio' && c.benchmarkType !== 'YOY' && this.oId === 5 && c.scope === 'portfolio')
                    || ['quadrantAnalysis'].includes(c.kpiType)) return
                    const { benchmarksAllowed, respondentTypesAllowed } = this.multiQuestionInfo
                    if (c.nonQuestionType === 'multiQuestion' && benchmarksAllowed.includes(c.chartType)) {
                        c.multiQuestion.forEach((q) => {
                            const { questionId, formId, responseType } = this.getMultiQuestionInfo(q)
                            if (!respondentTypesAllowed.includes(responseType)) {
                                return
                            }
                            const groupId = `${String(chartIndex)}_${period.value}_${questionId}`
                            this.setMultiQuestionMap(chartIndex, q)
                            const respondentType = c.respondentType || this.respondentType

                            body.widget.push({
                                responseType,
                                industry: c.industry || this.industry,
                                productType: c.productType || this.productType,
                                surveyType: c.surveyType || this.surveyType,
                                respondentType,
                                questionId,
                                formId,
                                scope: b === 'sector' ? 'industry' : b,
                                benchmarkType: period.benchmarkType,
                                divisionId: this.oId >= 4 && b === 'division' ? currentDivisionId || this.recordId : undefined,
                                portfolioId: this.oId >= 5 && b === 'portfolio' ? currentPortfolioId || this.recordId : undefined,
                                productId: this.oId === 6 && b === 'product' ? this.recordId : undefined,
                                groupId,
                                period: period.period,
                                parentId: c.qlistParentId,
                                termId: b === 'sector' && this.sectorId ? this.sectorId : undefined,
                                filter: respondentType === this.filterRespondentType ? {
                                    state: ['completed'],
                                    surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                                    ...R.omit(['openEnded'], this.filters),
                                } : undefined,
                            })
                        })
                    } else if (c.nonQuestionType !== 'multiQuestion') {
                        const responseType = c.parentQuestion && !c.questionTypeCode.includes('TB') ? 'pct' : c.kpiType
                        const respondentType = c.respondentType || this.respondentType
                        body.widget.push({
                            responseType,
                            industry: c.industry || this.industry,
                            productType: c.productType || this.productType,
                            surveyType: c.surveyType || this.surveyType,
                            respondentType,
                            questionId: c.parentQuestion || c.questionId,
                            formId: c.formId,
                            scope: b === 'sector' ? 'industry' : b,
                            benchmarkType: period.benchmarkType,
                            divisionId: this.oId >= 4 && b === 'division' ? currentDivisionId || this.recordId : undefined,
                            portfolioId: this.oId >= 5 && b === 'portfolio' ? currentPortfolioId || this.recordId : undefined,
                            productId: this.oId === 6 && b === 'product' ? this.recordId : undefined,
                            groupId: `${String(chartIndex)}_${period.value}`,
                            period: responseType === 'OSS' ? period.ossPeriod : period.period,
                            parentId: c.qlistParentId,
                            termId: b === 'sector' && this.sectorId ? this.sectorId : undefined,
                            filter: respondentType === this.filterRespondentType ? {
                                state: ['completed'],
                                surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                                ...R.omit(['openEnded'], this.filters),
                            } : undefined,
                        })
                    }
                })
            })

            if (c.nonQuestionType !== 'multiQuestion' && c.kpiType !== 'OSS') {
                this.tagBenchmarks.forEach(({ tagOptionId, customTag, scope }) => {
                    this.benchmarkPeriods.forEach((period) => {
                        const responseType = c.parentQuestion && !c.questionTypeCode.includes('TB') ? 'pct' : c.kpiType
                        const respondentType = c.respondentType || this.respondentType
                        body.widget.push({
                            responseType,
                            industry: c.industry || this.industry,
                            productType: c.productType || this.productType,
                            surveyType: c.surveyType || this.surveyType,
                            respondentType,
                            questionId: c.parentQuestion || c.questionId,
                            formId: c.formId,
                            scope: scope === 'company' ? 'client' : scope,
                            benchmarkType: period.benchmarkType,
                            divisionId: undefined,
                            portfolioId: undefined,
                            productId: undefined,
                            groupId: `${String(chartIndex)}_${period.value}`,
                            period: responseType === 'OSS' ? period.ossPeriod : period.period,
                            customTag,
                            tagOptions: [tagOptionId],
                            parentId: c.qlistParentId,
                            filter: respondentType === this.filterRespondentType ? {
                                state: ['completed'],
                                surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                                ...R.omit(['openEnded'], this.filters),
                            } : undefined,
                        })
                    })
                })
            }
        })
        let benchmarkData

        try {
            benchmarkData = await appStore.http.post(process.env.USE_NEW_DASHBOARD_ENDPOINT && this.useNewReport ? '/report/v1/dashboard' : '/v2/dashboard', JSON.stringify(body))
        } catch (e) {
            benchmarkData = { widget: body.widget.map((w) => ({ groupId: w.groupId, data: [] })) }
        }

        return benchmarkData.widget.reduce((bd, d) => {
            const [groupId, benchmarkPeriod] = d.groupId.split('_')
            if (!bd[groupId]) {
                return { ...bd, [groupId]: [{ ...d, benchmarkPeriod }] }
            }
            return { ...bd, [groupId]: [...bd[groupId], { ...d, benchmarkPeriod }] }
        }, {})
    }

    getChartDataPeriod = (chart, responseType) => {
        const { scope, benchmarkType } = chart

        const bcBenchmarkType = this.benchmarkTypeBackwardsCompat[benchmarkType] || benchmarkType
        if (!bcBenchmarkType.includes('last') && !bcBenchmarkType.includes('ROL')) return undefined
        const [_, months] = bcBenchmarkType.split('-')
        return scope === 'product' || responseType === 'OSS'
            ? { periodType: 'last', interval: 'MONTH', duration: Number(months) }
            : { periodType: 'ROL', duration: Number(months) }
    }

    getDetailsType = (data, nonQuestionType) => {
        const responseType = R.pathOr('', [0, 'responseType'], data)
        if (R.isNil(nonQuestionType) || nonQuestionType !== 'KPI') return responseType

        const dataType = R.pathOr(0, [0, 'data', 0, 'data'], data)

        return typeof dataType === 'object' ? 'pct' : 'avg'
    }

    getMenuOptions = () => {
        const { appStore, dashboard } = this
        const { userData, isExploriAdmin } = appStore
        const { role } = userData
        const { value } = dashboard
        const options = [
            {
                title: i18n.t('dashboard.menu.rename'),
                icon: 'fal fa-edit',
                onClick: () => { this.showDashboardRename = true },
                accessRights: () => ![170, 150].includes(role) && this.editing,
            },
            {
                title: i18n.t('dynamicDash.setFilters'),
                icon: 'fal fa-filter',
                onClick: () => { this.toggleShowFilters() },
                accessRights: () => ![170, 150].includes(role) && this.appStore.isExploriAdmin() && this.editing && this.oId === 6,
            },
            {
                title: i18n.t('dashboard.menu.duplicate'),
                icon: 'fal fa-clone',
                onClick: () => { this.showDashboardClone = true },
                accessRights: () => ![170, 150].includes(role) && this.editing,
            },
            {
                title: i18n.t('dashboard.menu.export'),
                icon: 'fal fa-download',
                onClick: () => { this.showDashboardExport = true },
            },
            {
                title: i18n.t('dashboard.menu.saveAsTab'),
                icon: 'fal fa-thumbtack',
                onClick: async () => {
                    try {
                        this.dashboardIsTab(this.dashboard)
                            ? await this.resetDashboardTab(this.dashboard)
                            : await this.setDashboardTab({
                                dashboard: this.dashboard,
                                name: this.dashboard.name,
                            })
                        this.init(true)
                        appStore.addNotification(null, 'Dashboard set as tab', 'success')
                    } catch (e) {
                        appStore.addNotification(null, 'Unable to update tabs', 'error')
                    }
                },
                render: ({
                    icon, title, className, onClick,
                }) => (
                    <li
                        className={className}
                        onClick={onClick}
                        style={{
                            gap: '0px',
                        }}
                    >
                        <div>
                            <i className={icon} />
                        </div>
                        <Checkbox
                            label={title}
                            checked={this.dashboardIsTab(this.dashboard)}
                            style={{
                                flexDirection: 'row-reverse',
                            }}
                        />
                    </li>
                ),
                accessRights: () => ![170, 150].includes(role) && isExploriAdmin() && this.editing,
            },
            {
                title: i18n.t('dashboard.menu.templateSettings'),
                icon: 'fal fa-wrench',
                onClick: () => {
                    if (this.dashboard.isTemplate) {
                        this.toggleEditingTemplate()
                    }
                    this.toggleShowCreateTemplateModal()
                },
                accessRights: () => isExploriAdmin() && this.editing,
            },
            {
                title: i18n.t('dashboard.menu.delete'),
                icon: 'fas fa-times',
                onClick: () => { this.showDashboardDelete = true },
                className: 'menu-option-greybg',
                accessRights: () => ![170, 150].includes(role) && !Number.isNaN(Number(value)) && this.editing,
            },
        ]

        return options
            .filter(({ accessRights }) => typeof accessRights !== 'function' || accessRights())
            .map(({
                title,
                icon,
                className,
                onClick,
                render,
            }) => (
                typeof render === 'function'
                    ? render({
                        onClick, className, title, icon,
                    })
                    : (
                        <li
                            className={className}
                            onClick={onClick}
                        >
                            <i className={icon} />
                            {title}
                        </li>
                    )
            ))
    }

    getActiveFilters = (respondentType, includeRegistrationData) => {
        const { dashboardFilterRuleGroups } = this

        if (!includeRegistrationData
            && !dashboardFilterRuleGroups[respondentType]?.question
            && !dashboardFilterRuleGroups[respondentType]?.openEnded
            && !this.urlSurveyDataIds.length) return undefined

        const filters = !dashboardFilterRuleGroups[respondentType] ? {
            surveyDataIds: this.urlSurveyDataIds,
            question: {
                ruleType: 'ruleGroup',
                rules: [],
            },
            campaign: {
                ruleType: 'ruleGroup',
                rules: [],
            },
            responseDate: {
                ruleType: 'ruleGroup',
                rules: [],
            },
        } : {
            surveyDataIds: dashboardFilterRuleGroups[respondentType].openEnded
                ? this.getSurveyDataIdFilter(dashboardFilterRuleGroups[respondentType].openEnded).map((v) => (typeof v === 'string' ? v.split('_') : v)).flat(2)
                : [],
            question: dashboardFilterRuleGroups[respondentType].question
                ? dashboardFilterRuleGroups[respondentType].question : {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
            campaign: {
                ruleType: 'ruleGroup',
                rules: [],
            },
            responseDate: {
                ruleType: 'ruleGroup',
                rules: [],
            },
        }

        const filterState = includeRegistrationData
            ? { state: ['completed', 'notStarted', 'dropped'] }
            : { state: ['completed'] }

        return {
            ...filterState,
            ...filters,
        }
    }

    getChartData = async (charts) => {
        const { appStore, widgets } = this
        const chartClone = charts || R.clone(widgets)
        const body = {
            widget: [],
        }
        this.refreshDashboardFilters()
        chartClone.forEach((c) => {
            const customTag = c.customTag ? c.customTag : undefined

            const pinnedCustomTagfilter = this.pinnedCustomTagFilters.find((t) => t.customTag === customTag)

            const tagOptions = process.env.USE_CUSTOM_BENCHMARKS === 'true'
                ? R.or(c.tagOptions, []).map((o) => o.value)
                    .filter((top) => !pinnedCustomTagfilter || R.or(pinnedCustomTagfilter.options, []).includes(top))
                : undefined

            if (['quadrantAnalysis', 'multiQuestion'].includes(c.nonQuestionType)) {
                c.multiQuestion.forEach((q) => {
                    const respondentType = q.respondentType || c.respondentType || this.respondentType
                    const { questionId, formId, responseType } = this.getMultiQuestionInfo(q)
                    const filters = this.getActiveFilters(respondentType, c.includeRegistrationData)

                    body.widget.push({
                        responseType,
                        industry: q.industry || c.industry || this.industry,
                        productType: q.productType || c.productType || this.productType,
                        surveyType: q.surveyType || c.surveyType || this.surveyType,
                        respondentType,
                        questionId: questionId || '00000000-0000-0000-0000-000000000000',
                        formId,
                        scope: c.scope || this.getChartScope(this.oId),
                        benchmarkType: c.benchmarkType.includes('last') || c.benchmarkType.includes('ROL') ? 'ROL' : c.benchmarkType,
                        divisionId: this.oId === 4 ? this.recordId : undefined,
                        portfolioId: this.oId === 5 ? this.recordId : undefined,
                        productId: this.oId === 6 ? this.recordId : undefined,
                        groupId: c.key,
                        period: this.getChartDataPeriod(c, responseType),
                        parentId: c.qlistParentId,
                        customTag,
                        tagOptions: tagOptions && tagOptions.length ? tagOptions : undefined,
                        filter: filters,
                    })
                })
                return
            }
            const respondentType = c.respondentType || this.respondentType

            const filters = this.getActiveFilters(respondentType, c.includeRegistrationData)

            body.widget.push({
                responseType: c.parentQuestion && !c.questionTypeCode.includes('TB') ? 'pct' : c.kpiType,
                industry: c.industry || this.industry,
                productType: c.productType || this.productType,
                surveyType: c.surveyType || this.surveyType,
                respondentType,
                questionId: c.parentQuestion || c.questionId,
                formId: c.formId,
                scope: c.scope || this.getChartScope(this.oId),
                benchmarkType: c.benchmarkType.includes('last') || c.benchmarkType.includes('ROL') ? 'ROL' : c.benchmarkType,
                divisionId: this.oId === 4 ? this.recordId : undefined,
                portfolioId: this.oId === 5 ? this.recordId : undefined,
                productId: this.oId === 6 ? this.recordId : undefined,
                groupId: c.key,
                period: this.getChartDataPeriod(c, c.parentQuestion && !c.questionTypeCode.includes('TB') ? 'pct' : c.kpiType),
                parentId: c.qlistParentId,
                customTag,
                tagOptions: tagOptions && tagOptions.length ? tagOptions : undefined,
                filter: filters,
            })
        })
        let chartData
        let benchmarkData
        try {
            [chartData, benchmarkData] = await Promise.all([
                appStore.http.post(process.env.USE_NEW_DASHBOARD_ENDPOINT && this.useNewReport ? '/report/v1/dashboard' : '/v2/dashboard', JSON.stringify(body)),
                this.getBenchmarkData(chartClone),
            ])
            this.benchmarkData = benchmarkData
        } catch (e) {
            chartData = { widget: body.widget.map((w) => ({ groupId: w.groupId, data: [] })) }
        }
        let currencyFetch
        try {
            currencyFetch = await appStore.http.get('/currency/dayrate')
        } catch (e) {
            currencyFetch = {}
        }
        this.todaysCurrencyRates = R.pathOr({}, ['rates'], currencyFetch)
        this.currencyConversions = chartData.exchangeRates

        const groupedChartData = R.groupBy((c) => c.groupId)(chartData.widget)

        const getBenchmarkName = (data) => {
            const { scope } = data
            const tagOptionId = R.pathOr(0, ['data', 0, 'tagOptionId'], data)
            const termId = R.pathOr(0, ['data', 0, 'termId'], data)
            if (tagOptionId > 0) return this.tagBenchmarkLabels[`${scope === 'client' ? 'company' : scope}_${tagOptionId}`]
            if (termId > 0) return this.benchmarkLabels.sector

            return this.benchmarkLabels[scope]
        }

        return chartClone.map((c, chartIndex) => ({
            ...c,
            data: [
                ...groupedChartData[c.key].map((d, i) => d.data.map((sd) => (
                    {
                        ...sd,
                        multiName: R.pathOr(undefined, ['multiQuestion', i, 'name'], c),
                        multiQuestionId: R.pathOr(undefined, ['multiQuestion', i, 'value'], c),
                    }
                ))).flat(1),
                ...R.pathOr([], [chartIndex], this.benchmarkData).map((d) => d.data.map((sd) => {
                    const [idx, period, qId] = d.groupId.split('_')
                    let multiName
                    if (qId) {
                        multiName = this.multiQuestionInfo.questions[idx].get(qId)
                    }
                    return (
                        {
                            ...sd,
                            benchmarkName: getBenchmarkName(d),
                            benchmark: d.scope,
                            benchmarkPeriod: d.benchmarkPeriod,
                            multiName,
                        }
                    )
                })).flat(1),
            ],
            dataDetails: {
                type: this.getDetailsType(groupedChartData[c.key], c.nonQuestionType),
            },
            options: groupedChartData[c.key].map((o) => o.options).flat(1),
            columns: groupedChartData[c.key].map((o) => o.columns).flat(1),
            min: groupedChartData[c.key].length === 1 ? R.pathOr(undefined, [c.key, 0, 'min'], groupedChartData) : undefined,
            max: groupedChartData[c.key].length === 1 ? R.pathOr(undefined, [c.key, 0, 'max'], groupedChartData) : undefined,
            percentiles: R.pathOr(undefined, [c.key, 0, 'percentiles'], groupedChartData),
            isCurrency: groupedChartData[c.key].some((o) => o.isCurrency),
            quadrantCurrency: c.nonQuestionType === 'quadrantAnalysis'
                ? c.multiQuestion.reduce((qc, q, i) => (
                    { ...qc, [q.name]: groupedChartData[c.key][i].isCurrency }
                ), {})
                : undefined,
        }))
    }

    getChartScope = (oid) => {
        switch (oid) {
            case 3:
                return 'client'
            case 4:
                return 'division'
            case 5:
                return 'portfolio'
            case 6:
                return 'event'
            default:
                return 'NoOID'
        }
    }

    getLastAllocatedRow = () => {
        const { layouts } = this
        const lastAllocatedRow = {
            lg: 0,
            md: 0,
            sm: 0,
            xs: 0,
            xxs: 0,
        }
        const breakpoints = Object.keys(this.responsiveCols)

        breakpoints.forEach((bp) => {
            layouts[bp].forEach((l) => {
                if (l.y > lastAllocatedRow[bp]) {
                    lastAllocatedRow[bp] = l.y
                }
            })
        })
        return lastAllocatedRow
    }

    remainingSpaceCalculator = (currentLayout) => {
        const breakpoints = Object.keys(this.responsiveCols)
        const remainingRowSpace = {}
        breakpoints.forEach((bp) => {
            if (!currentLayout[bp]) return
            currentLayout[bp].forEach((el) => {
                remainingRowSpace[bp] = remainingRowSpace[bp] || {}
                remainingRowSpace[bp][el.y] = remainingRowSpace[bp][el.y] || this.responsiveCols[bp]
                remainingRowSpace[bp][el.y] -= el.w
            })
        })
        return remainingRowSpace
    }

    nextSpaceCalculator = (newElWidth, layout) => {
        const freeSpaces = this.remainingSpaceCalculator(layout)

        const breakpoints = Object.keys(this.responsiveCols)

        let rowFound
        let newRow
        let newColumn
        const newCoordinates = {}

        breakpoints.forEach((bp) => {
            newRow = undefined
            newColumn = undefined
            rowFound = false
            if (freeSpaces[bp]) {
                rowFound = Object.keys(freeSpaces[bp]).some((row) => {
                    if (freeSpaces[bp][row] >= newElWidth
                        && parseInt(row, 10) >= this.lastAllocatedRow[bp]) {
                        newRow = parseInt(row, 10)
                        newColumn = this.responsiveCols[bp] - freeSpaces[bp][row]
                        return true
                    }
                    return false
                })
            }
            if (!rowFound) {
                newRow = freeSpaces[bp]
                    ? Object.keys(freeSpaces[bp]).length
                    : 0
                newColumn = 0
            }
            this.lastAllocatedRow[bp] = newRow
            newCoordinates[bp] = {
                x: newColumn, y: newRow, w: newElWidth, h: 1,
            }
        })
        return newCoordinates
    }

    addNewElementLayout = (element, layout, key) => {
        const breakpoints = Object.keys(this.responsiveCols)
        const coordinates = this.nextSpaceCalculator(element.width, layout)
        breakpoints.forEach((bp) => {
            layout[bp].push({ ...coordinates[bp], i: key, static: this.staticGridItems })
        })
        return layout
    }

    changeYears = ({ value }) => {
        const allowedYears = value.map((y) => y.value)
        Array.from(this.years.keys()).forEach((ty) => {
            this.years.set(ty, allowedYears.includes(ty))
        })
    }

    changeBenchmarks = ({ value }) => {
        const allowedBenchmarks = value.map((b) => b.value)
        Array.from(this.benchmarks.keys()).forEach((ty) => {
            this.benchmarks.set(ty, allowedBenchmarks.includes(ty))
        })
    }

    changeBenchmarkPeriod = ({ value }) => {
        this.benchmarkPeriod = value
    }

    benchmarkCheck = (dataObj, allowedBenchmarks, elementBenchmarks) => {
        const { benchmarkPeriod } = this
        if (!dataObj.benchmark) return true
        if (!allowedBenchmarks) return false
        if (dataObj.benchmarkPeriod !== benchmarkPeriod) return false
        if (!elementBenchmarks.includes(dataObj.benchmark)) return false
        if (dataObj.tagOptionId && dataObj.tagOptionId !== 0) return Boolean(this.benchmarks.get(`${dataObj.benchmark === 'client' ? 'company' : dataObj.benchmark}_${dataObj.tagOptionId}`))
        if (dataObj.termId) return Boolean(this.benchmarks.get('sector'))
        return this.benchmarks.has(dataObj.benchmark)
            ? this.benchmarks.get(dataObj.benchmark) : true
    }

    yearCheck = (dataObj) => {
        const { years } = this
        const { year } = dataObj
        return year && years.has(year) ? years.get(year) : true
    }

    productFilterCheck = (dataObj, productFilters) => {
        const { productId, benchmark } = dataObj
        if (!productFilters.length) return true
        return productFilters.includes(productId) || benchmark
    }

    portfolioFilterCheck = (dataObj, portfolioFilters) => {
        const { portfolioId, benchmark } = dataObj
        if (!portfolioFilters.length) return true
        return portfolioFilters.includes(portfolioId) || benchmark
    }

    divisionFilterCheck = (dataObj, divisionFilters) => {
        const { divisionId, benchmark } = dataObj
        if (!divisionFilters.length) return true
        return divisionFilters.includes(divisionId) || benchmark
    }

    setTopTwoYears = () => {
        if (this.years.size < 3 || !this.years.size) return
        Array.from(this.years.keys()).sort().reverse().forEach((k, i) => {
            this.years.set(k, i < 2)
        })
    }

    resetYears = () => {
        this.years = new Map()
    }

    @action
    formatData = (data, element) => {
        data.forEach((d) => {
            if (d.year && !this.years.has(d.year) && d.year >= 1900) {
                this.years.set(d.year, true)
                this.setTopTwoYears()
            }
        })
        const productFilters = element.scope === 'product' ? R.pathOr([], ['filter'], element).map((f) => f.productId) : []
        const portfolioFilters = element.scope === 'portfolio' ? R.pathOr([], ['filter'], element).map((f) => f.portfolioId) : []
        const divisionFilters = element.scope === 'division' ? R.pathOr([], ['filter'], element).map((f) => f.divisionId) : []
        const allowedBenchmarks = !['treemap', 'pie', 'donut'].includes(element.chartType)
        const elementBenchmarks = R.pathOr(this.possibleBenchmarks[this.oId], ['widgetRenderSettings', 'allowedBenchmarks'], element)

        const filteredData = data.filter((d) => (
            this.benchmarkCheck(d, allowedBenchmarks, elementBenchmarks)
            && this.productFilterCheck(d, productFilters)
            && this.portfolioFilterCheck(d, portfolioFilters)
            && this.divisionFilterCheck(d, divisionFilters)
            && this.yearCheck(d)
        ))
        return this.dataFormatter.formatData(filteredData, element)
    }

    compareValues = (value1, value2) => {
        const v1 = value1 ? Number(value1) : 0
        const v2 = value2 ? Number(value2) : 0
        if (v2 > v1) {
            return { className: 'gt', icon: '↗' }
        }
        if (v2 < v1) {
            return { className: 'lt', icon: '↘' }
        }
        if (v2 === v1) {
            return { className: 'eq', icon: '─' }
        }
        return { className: '', icon: '' }
    }

     getCurrencySymbol = (currency, locale = 'en-GB') => {
         let currencySymbol
         try {
             currencySymbol = (0).toLocaleString(
                 locale,
                 {
                     style: 'currency',
                     currency,
                     minimumFractionDigits: 0,
                     maximumFractionDigits: 0,
                 },
             ).replace(/\d/g, '').trim().slice(-1)
         } catch (e) {
             currencySymbol = currency
         }
         return currencySymbol
     }

    renderNoDataElement = () => (
        <div style={{
            width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
        }}
        >
            <img style={{ width: '150px' }} src={`${process.env.CDN_URL}/images/search-icon-v2.svg`} />
            <div style={{
                fontSize: '20px', marginTop: '10px', fontWeight: '700', color: '#8B99A7',
            }}
            >
                No data has been found
            </div>
        </div>
    )

    getChart = (element, data, yearFilters, benchmarkFilters, benchmarkPeriod) => {
        let noData = !data || !data.filter((d) => !d.benchmark).length
        const { fData, xAxisLabels } = this.formatData(
            data, element, yearFilters, benchmarkFilters, benchmarkPeriod,
        )
        if (!fData || R.isEmpty(fData)) noData = true
        if (noData) return this.renderNoDataElement()

        const currency = R.pathOr('$', ['widgetRenderSettings', 'currency'], element)
        const format = R.pathOr(element.isCurrency ? 'currency' : undefined, ['widgetRenderSettings', 'format'], element)
        const dataFormat = format === 'currency' ? this.getCurrencySymbol(currency) : format
        const xAxisRenderLabels = xAxisLabels && Array.isArray(xAxisLabels)
            ? xAxisLabels.map((l) => R.pathOr(l, ['widgetRenderSettings', 'xAxis', l], element))
            : xAxisLabels

        switch (element.chartType) {
            case 'donut':
            case 'pie': {
                const compact = R.pathOr(Number(R.pathOr(2, ['width'], element)) < 4 ? 'vertical' : 'horizontal',
                    ['widgetRenderSettings', 'orientation'],
                    element) === 'vertical'
                const renderSettings = chartRenderSettings(element, this, xAxisLabels).layoutEdit
                return (
                    <Pie
                        chartData={R.pathOr(fData, [0, 'data'], fData)
                            .map((d) => ({ ...d, name: R.pathOr(d.name, ['widgetRenderSettings', 'option', d.name], element) }))}
                        style={{ width: '100%', height: '100%' }}
                        compact={compact}
                        donut={element.chartType === 'donut'}
                        dataFormat={dataFormat}
                        key={element.key}
                        tooltipDecimals={R.pathOr(0, ['widgetRenderSettings', 'tooltipDecimals'], element)}
                        layoutEdit={[...renderSettings]}
                    />
                )
            }
            case 'treemap': {
                const renderSettings = chartRenderSettings(element, this, xAxisLabels, fData)
                return (
                    <Treemap
                        chartData={R.pathOr(fData, [0, 'data'], fData)}
                        style={{ width: '100%', height: '100%' }}
                        compact={Number(R.pathOr(2, ['width'], element) < 4)}
                        donut={element.chartType === 'donut'}
                        dataFormat={dataFormat}
                        tooltipDecimals={R.pathOr(0, ['widgetRenderSettings', 'tooltipDecimals'], element)}
                        key={element.key}
                        layoutEdit={[...renderSettings.layoutEdit]}
                    />
                )
            }
            case 'bar':
            case 'column': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], element)

                const renderSettings = chartRenderSettings(element, this, xAxisLabels, fData)
                console.log('RENDER', renderSettings)
                return (
                    <Bar
                        xAxisLabels={renderSettings.finalxAxisLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        transpose={element.chartType === 'bar'}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        showLegend
                        dataFormat={dataFormat}
                        key={element.key}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        defaultSeries={renderSettings.defaultSeries || {
                            type: 'bar',
                            label: {
                                show: false,
                            },
                            emphasis: {
                                focus: 'series',
                            },
                            data: [],
                        }}
                    />
                )
            }
            case 'stackedBar':
            case 'stackedColumn': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], element)

                const renderSettings = chartRenderSettings(element, this, xAxisLabels, fData)
                return (
                    <Bar
                        xAxisLabels={xAxisRenderLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        transpose={element.chartType === 'stackedBar'}
                        showLegend
                        layoutEdit={[...renderSettings.layoutEdit]}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        dataFormat={dataFormat}
                        key={element.key}
                        defaultSeries={renderSettings.defaultSeries || {
                            name: '',
                            type: 'bar',
                            stack: 'data',
                            label: {
                                show: false,
                            },
                            emphasis: {
                                focus: 'series',
                            },
                            data: [],
                        }}
                    />
                )
            }
            case 'line': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], element)

                const renderSettings = chartRenderSettings(element, this, xAxisLabels)
                return (
                    <Line
                        xAxisLabels={xAxisRenderLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        showLegend
                        layoutEdit={[...renderSettings.layoutEdit]}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        dataFormat={dataFormat}
                        key={element.key}
                        defaultSeries={renderSettings.defaultSeries || undefined}
                    />
                )
            }
            case 'text': {
                const sortedData = sortBy(fData, 'year', -1).sort((a, b) => {
                    if (a.benchmark && !b.benchmark) return 1
                    if (!a.benchmark && b.benchmark) return -1
                    return 0
                })
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))

                    const dataLatestYear = R.pathOr(
                        R.pathOr(0, [0, 'data', 0, 'year'], sortedData),
                        [0, 'year'],
                        sortedData,
                    )

                    if (latestSelectedYear !== dataLatestYear) {
                        if (element.questionTypeCode === 'TB - row') {
                            sortedData[0].data
                                .unshift({ year: latestSelectedYear, value: undefined })
                        } else {
                            sortedData.unshift({ year: latestSelectedYear, value: undefined })
                        }
                    }
                }
                const benchmarkData = R.remove(0, 1, R.or(sortedData, [0]))

                const mainDataYear = R.pathOr(0, [0, 'year'], sortedData)

                const chartCenterData = Number(R.pathOr(
                    R.pathOr(undefined, [0, 'data', 0, 'value'], sortedData),
                    [0, 'value'],
                    sortedData,
                ))

                const centerText = (mainDataYear && Number(mainDataYear) !== 84
                    ? (
                        `${mainDataYear < 1900 ? 'Last' : 'Year:'} ${mainDataYear} ${mainDataYear < 1900 ? 'months' : ''}`
                    )
                    : '')

                return (
                    <Text
                        mainText={Number.isNaN(chartCenterData) ? (
                            <div className="noData">
                                No data
                                <br />
                                available
                            </div>
                        ) : chartCenterData}
                        subText={centerText}
                        benchmarks={benchmarkData.map(({ value, benchmarkName, year }) => {
                            let icon = '─'
                            let iconColor = '#FEB200'
                            if (Number.isNaN(chartCenterData)) {
                                icon = '↗'
                                iconColor = '#16D2C6'
                            } else if (Number(value).toFixed(2) !== Number(chartCenterData).toFixed(2)) {
                                icon = chartCenterData > value ? '↘' : '↗'
                                iconColor = chartCenterData > value ? '#F46957' : '#16D2C6'
                            }
                            return ({
                                name: benchmarkName || year,
                                value: (
                                    <span style={{ color: iconColor }}>
                                        {Number(value)}
                                        {' '}
                                        {icon}
                                    </span>
                                ),
                            })
                        })}
                        style={{ width: '100%', height: '100%' }}
                        dataFormat={Number.isNaN(chartCenterData) ? undefined : dataFormat}
                        dataDecimals={R.pathOr(2, ['widgetRenderSettings', 'dataDecimals'], element)}
                        key={element.key}
                    />
                )
            }
            case 'gauge0-5':
            case 'gauge': {
                const sortedData = sortBy(fData, 'year', -1).sort((a, b) => {
                    if (a.benchmark && !b.benchmark) return 1
                    if (!a.benchmark && b.benchmark) return -1
                    return 0
                })
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))

                    const dataLatestYear = R.pathOr(
                        R.pathOr(0, [0, 'data', 0, 'year'], sortedData),
                        [0, 'year'],
                        sortedData,
                    )

                    if (latestSelectedYear !== dataLatestYear) {
                        sortedData.unshift({ year: latestSelectedYear, value: undefined })
                    }
                }
                const benchmarkData = R.remove(0, 1, R.or(sortedData, [0]))
                const renderSettings = chartRenderSettings(
                    element, this, xAxisLabels, { sortedData, benchmarkData },
                )
                const mainDataYear = R.pathOr(0, [0, 'year'], sortedData)

                const chartCenterData = Number(R.pathOr(
                    R.pathOr(undefined, [0, 'data', 0, 'value'], sortedData),
                    [0, 'value'],
                    sortedData,
                ))

                const centerText = (mainDataYear && Number(mainDataYear) !== 84
                    ? (
                        `${mainDataYear < 1900 ? 'Last' : 'Year:'} ${mainDataYear} ${mainDataYear < 1900 ? 'months' : ''}`
                    )
                    : '')

                const min = element.kpiType === 'nps' ? -100 : element.min
                const max = element.kpiType === 'nps' ? 100 : element.max
                return (
                    <Gauge
                        chartData={(chartCenterData || 0)}
                        style={{ width: '100%', height: '100%' }}
                        containerSize={element.containerSize}
                        min={element.chartType === 'gauge0-5' ? min || 0 : min}
                        max={element.chartType === 'gauge0-5' ? max || 5 : max}
                        dataFormat={dataFormat}
                        key={element.key}
                        dataDecimals={R.pathOr(element.chartType === 'gauge0-5' ? 2 : 0, ['widgetRenderSettings', 'dataDecimals'], element)}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        centerText={centerText}
                    />
                )
            }
            case 'radar': {
                const renderSettings = chartRenderSettings(element, this, xAxisLabels, fData).layoutEdit
                return (
                    <Radar
                        chartData={fData.map((fd) => ({ value: fd.data.map((d) => d.value), name: fd.name }))}
                        indicators={fData[0].data.map((d) => ({ name: d.name }))}
                        style={{ width: '100%', height: '100%' }}
                        radarMax={5}
                        dataFormat={dataFormat}
                        key={element.key}
                        tooltipDecimals={R.pathOr(0, ['widgetRenderSettings', 'tooltipDecimals'], element)}
                        layoutEdit={[
                            { path: ['series', 0, 'areaStyle'], value: undefined },
                            ...renderSettings,
                        ]}
                        showLegend
                    />
                )
            }
            case 'lmx': {
                const sortedData = sortBy(fData, 'year', -1).sort((a, b) => {
                    if (a.benchmark && !b.benchmark) return 1
                    if (!a.benchmark && b.benchmark) return -1
                    return 0
                })
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))

                    const dataLatestYear = R.pathOr(
                        R.pathOr(0, [0, 'data', 0, 'year'], sortedData),
                        [0, 'year'],
                        sortedData,
                    )

                    if (latestSelectedYear !== dataLatestYear) {
                        sortedData.unshift({
                            year: latestSelectedYear,
                            data: [
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                            ],
                        })
                    }
                }

                let dataWithBenchmarks = []

                const benchmarkData = R.remove(0, 1, R.or(sortedData, [0]))
                if (benchmarkData.length) {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData).map((d, i) => ({
                        ...d,
                        value: d.value ? `${Number(d.value)}%` : '',
                        benchmarks: benchmarkData.map((bd, bdI) => {
                            if (bdI > 5) return undefined
                            const { className, icon } = this.compareValues(R.pathOr(0, ['value'], d), bd.data[i].value)
                            return (
                                {
                                    name: bd.benchmarkName || bd.year,
                                    value: (
                                        <span className={`benchmarkValue ${className}`}>
                                            {bd.data[i].value}
                                            %
                                            {' '}
                                            {icon}
                                        </span>
                                    ),
                                }
                            )
                        }).filter((fbd) => !!fbd),
                    }))
                } else {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData)
                        .map((d) => ({ ...d, value: d.value ? `${Number(d.value)}%` : '' }))
                }

                return (
                    <MatrixChart
                        chartData={dataWithBenchmarks.map((d) => ({
                            ...d,
                            name: startCase(d.name),
                        }))}
                        style={{ width: '100%', height: '100%' }}
                        key={element.key}
                        matrixAxisNames={R.pathOr({}, ['widgetRenderSettings', 'matrixAxisNames'], element)}
                        containerSize={element.containerSize}
                    />
                )
            }
            case 'quadrantAnalysis': {
                const showLabels = R.pathOr(false, ['widgetRenderSettings', 'showLabels'], element)

                const renderSettings = chartRenderSettings(element, this, xAxisLabels)

                const dataFormatObj = {
                    x: R.pathOr(false, ['quadrantCurrency', 'xAxis'], element) ? dataFormat : undefined,
                    y: R.pathOr(false, ['quadrantCurrency', 'yAxis'], element) ? dataFormat : undefined,
                    z: R.pathOr(false, ['quadrantCurrency', 'zAxis'], element) ? dataFormat : undefined,
                }

                return (
                    <div style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    >
                        <QuadrantHeader
                            key={`${element.key}quadrantHeader`}
                            dashStore={this}
                            widget={element}
                        />
                        <div style={{ height: '100%' }} className="quadrantAnalysisChartContainer">
                            <QuadrantAnalysis
                                chartData={fData}
                                style={{ width: '100%', height: '100%' }}
                                questionNames={xAxisLabels}
                                showLabels={showLabels}
                                dataFormat={dataFormatObj}
                                layoutEdit={[...renderSettings.layoutEdit]}
                                key={element.key}
                            />
                        </div>
                    </div>
                )
            }
            case 'multiText': {
                let filteredData = fData
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))
                    filteredData = fData.filter((d) => d.year === latestSelectedYear)
                }

                return (
                    <div>
                        <MultiText
                            key={element.key}
                            chartData={filteredData.map((d) => (
                                <div style={{
                                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center',
                                }}
                                >
                                    <div style={{ fontSize: '30px', fontWeight: 700 }}>{Number(R.pathOr(0, ['value'], d))}</div>
                                    <div style={{
                                        font: 'normal normal bold 15px/15px',
                                        letterSpacing: '0.2px',
                                        color: '#16CCC1',
                                        marginBottom: '18px',
                                    }}
                                    >
                                        {R.pathOr('', ['name'], d).split(' - ')[R.pathOr('', ['name'], d).split(' - ').length - 1]}
                                    </div>
                                    <div style={{
                                        width: '53px',
                                        height: '0px',
                                        borderTop: '3px dotted #ABEEEA',
                                    }}
                                    />
                                </div>

                            ))}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                )
            }
            case 'textResponse': {
                const { langcode } = this.appStore
                const years = fData.map((d) => d.year)
                const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], element)
                const title = R.pathOr(false, ['widgetRenderSettings', 'title'], element)
                const titleText = R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text
                const iconColour = R.pathOr('#16CCC1', ['widgetRenderSettings', 'colors', 0], element)
                return (
                    <TextResponseChart
                        chartData={fData}
                        key={element.key}
                        title={showTitle ? unescapeHtmlTags(titleText) : ''}
                        years={years}
                        defaultYear={Math.max(...years)}
                        height={element.containerSize.height - 79} // 79 is widget header plus 30 padding
                        iconColour={iconColour}
                    />
                )
            }
            case 'wordCloud': {
                const years = fData.map((d) => d.year)
                const latestYear = Math.max(...years)
                return (
                    <WordCloud
                        data={fData ? this.formatWordCloudData(fData, element, Math.max(...years)) : []}
                        style={{ width: '100%', height: '100%' }}
                        maskImage={this.wordCloudMaskimage}
                        pauseRender={element.key === this.resizingElement}
                        key={`${element.key}_${latestYear}`}
                    />
                )
            }
            default:
                return null
        }
    }

    formatWordCloudData = (allData, element, year) => {
        const cacheKey = `${element.key}_${year}_${this.filterChangeKey}`
        const data = allData.find((d) => d.year === year)
        if (!data) return []
        if (this.wordCloudData && this.wordCloudData[cacheKey]) return this.wordCloudData[cacheKey]

        const defaultColors = [
            '#16CCC1',
            '#715AF8',
            '#1A81F0',
            '#97D100',
            '#FFDA10',
            '#FEB200',
            '#FD597F',
            '#D9314B',
        ]
        let text = ''
        data.data.forEach((d) => {
            text = `${text} ${d.response}`
        })
        const words = removeStopwords(text
            .toLowerCase()
            .replace(/[^\w\s]/g, '')
            .split(/\s+/))

        const wordCount = {}

        words.forEach((word) => {
            if (word) {
                wordCount[word] = (wordCount[word] || 0) + 1
            }
        })

        const topWords = Object.entries(wordCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 500)

        const finalData = topWords.map(([word, count], i) => (
            {
                name: word,
                value: count,
                textStyle: { color: defaultColors[i % 8] },
            }
        ))

        this.wordCloudData = {
            ...this.wordCloudData,
            [cacheKey]: finalData,
        }

        return finalData
    }

    createTreeData = (level, filters) => {
        const { appStore } = this
        const defaultValues = filters.map((f) => f.value)

        const groupedProducts = this.products.reduce((obj, p) => {
            obj[p.portfolioId] = obj[p.portfolioId] || []
            obj[p.portfolioId].push({
                ...p,
                value: `products_${p.productId}`,
                label: p.name,
                checked: defaultValues.includes(`products_${p.productId}`),
            })
            return obj
        }, {})
        const groupedPortfolios = this.portfolios.reduce((obj, p) => {
            obj[p.divisionId] = obj[p.divisionId] || []
            obj[p.divisionId].push({
                ...p,
                children: level === 'product' ? groupedProducts[p.portfolioId] : undefined,
                value: `portfolios_${p.portfolioId}`,
                label: p.name,
                checked: defaultValues.includes(`portfolios_${p.portfolioId}`) || R.pathOr([], [p.portfolioId], groupedProducts).every((pr) => pr.checked),
            })
            return obj
        }, {})
        const groupedDivisions = this.divisions.reduce((arr, d) => {
            arr.push({
                ...d,
                children: ['product', 'portfolio'].includes(level) ? groupedPortfolios[d.divisionId] : undefined,
                value: `divisions_${d.divisionId}`,
                label: d.name,
                checked: defaultValues.includes(`divisions_${d.divisionId}`),
            })
            return arr
        }, [])

        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)

        const filterTree = isMultiDivision ? groupedDivisions : Object.values(groupedPortfolios)[0]
        this.filterTree = filterTree
        return filterTree
    }

    addWidget = async (widget) => {
        const { layouts } = this
        const newWidget = { ...widget, key: uuidv4() }
        const widgetClone = [...this.widgets.slice(), newWidget]

        const newLayout = this.addNewElementLayout(newWidget, layouts, newWidget.key)
        this.widgets = await this.getChartData(widgetClone)
        this.layouts = newLayout
        this.editing = true
    }

    removeChart = (chartKey) => {
        this.editing = true
        this.widgets = this.widgets.filter((w) => w.key !== chartKey)
        this.layouts = Object.keys(this.layouts).reduce((layout, k) => {
            layout[k] = this.layouts[k].filter((l) => l.i !== chartKey)
            return layout
        }, {})
    }

    onLayoutChange = (layout, allLayouts) => {
        this.layouts = allLayouts
    }

    editDashboard = () => {
        this.editing = true
        this.trackEditAction('started')
    }

    resetDefaults = async (setScope, setLevel) => {
        const {
            dashboards, appStore,
        } = this
        const { clientId } = appStore.userData

        const filteredDashboards = dashboards.filter((d) => (
            d.clientId === clientId
            && R.pathOr(undefined, ['widgets', 'defaultDashSettings', 'oid'], d) === setScope
            && R.pathOr(undefined, ['widgets', 'defaultDashSettings', 'level'], d) === setLevel
        ))

        await Promise.all(filteredDashboards.map((dash) => {
            const {
                name, oId, recordId, userId, isPublic, widgets, value: dashboardId, productType,
            } = dash

            const body = {
                dashboardId,
                name,
                clientId,
                oId,
                recordId,
                userId,
                isPublic,
                productType,
                widgets: JSON.stringify({
                    ...widgets,
                    defaultDashSettings: { oid: undefined, level: undefined },
                }),
            }
            return appStore.http.put(`/dashboard/${dashboardId}`, JSON.stringify(body))
        }))
    }

    setDefaults = async (dashboardId, setScope, setLevel, resetOnly, makeTemplate) => {
        const {
            dashboards, appStore,
        } = this
        try {
            await this.resetDefaults(setScope, setLevel)

            if (resetOnly) {
                appStore.addNotification(null, 'Updated to use Explori default dashboard', 'success')
                return
            }
            if (!dashboardId) {
                appStore.addNotification(null, 'Error Updating Dashboard No Dashboard Id', 'error')
                return
            }

            const dashboard = dashboards.find((d) => d.dashboardId === dashboardId)

            const {
                name, clientId, oId, recordId, userId, isPublic, widgets,
            } = dashboard

            const productType = oId !== 6 ? undefined : this.productType

            const body = {
                dashboardId,
                name,
                clientId,
                oId,
                recordId: makeTemplate ? 0 : recordId,
                userId,
                isPublic,
                productType,
                widgets: JSON.stringify({
                    ...widgets,
                    defaultDashSettings: { oid: setScope, level: setLevel },
                }),
            }

            await appStore.http.put(`/dashboard/${dashboardId}`, JSON.stringify(body))
            appStore.addNotification(null, 'Default Dashboard Updated', 'success')
        } catch (e) {
            appStore.addNotification(null, 'Error Updating Dashboard', 'error')
        }
    }

    createNewDashboard = async (dashName, global = false, isTemplate = false, templateSettings = undefined) => {
        const {
            appStore,
        } = this
        const isGlobal = typeof global === 'boolean' && global === true
        const productType = this.oId !== 6 ? undefined : this.productType
        const body = {
            dashboardId: 0,
            name: dashName,
            clientId: appStore.userData.clientId,
            oId: this.oId,
            recordId: isGlobal ? 0 : this.recordId,
            userId: appStore.userData.userId,
            isPublic: true,
            isTemplate: !!isTemplate,
            productType,
            widgets: JSON.stringify({
                widgets: [],
                layouts: this.defaultLayout,
                defaultDashSettings: { level: dashName },
                templateSettings,
            }),
        }

        try {
            const newDashboard = await appStore.http.post('/dashboard', JSON.stringify(body))
            appStore.addNotification(null, 'Dashboard Created', 'success')

            await this.getDashboards(newDashboard.dashboardId)
            this.trackEditAction('saved', { is_new_dashboard: true })
            this.editing = false
        } catch (e) {
            appStore.addNotification(null, 'Error Creating Dashboard', 'error')
        }
    }

    hideTemplateCloneModal = () => {
        this.showTemplateCloneModal = false
    }

    duplicateDashFromTemplate = () => {
        this.hideTemplateCloneModal()
        this.showDuplicateModal = true
    }

    hideDuplicateModal = () => {
        this.showDuplicateModal = false
    }

    toggleManageDashboardsModal = () => {
        this.showManageDashboardsModal = !this.showManageDashboardsModal
    }

    beginSaveDashboard = () => {
        const { appStore } = this

        if ((this.oId === 3 || this.dashboard.recordId !== 0)
            && this.dashboard.clientId === appStore.userData.clientId) {
            this.saveDashboard()
            return
        }
        this.showTemplateCloneModal = true
    }

    deleteDashboard = async () => {
        const { dashboard, appStore } = this
        const { value } = dashboard

        try {
            await appStore.http.delete(`/dashboard/${value}`)
            appStore.addNotification(null, 'Dashboard deleted', 'success')
            this.init(true)
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error deleting Dashboard', 'error')
        }
    }

    saveDashboard = async (cloning = false, cloneName, dashPrivacy) => {
        const {
            dashboard, layouts, widgets, appStore,
        } = this
        const {
            name, clientId, oId, recordId, userId, isPublic, value, widgets: dashboardWidgetsField, isTemplate,
        } = dashboard
        const clone = typeof cloning === 'boolean' && cloning === true
        const productType = oId !== 6 ? undefined : this.productType

        let newName = name
        if (!R.isNil(cloneName)) {
            newName = cloneName
        }

        let rId = clone ? this.recordId : recordId

        if (dashPrivacy === 'public') {
            rId = 0
        }

        const body = {
            dashboardId: value === 'new' || clone ? 0 : value,
            name: newName,
            clientId: clone ? appStore.userData.clientId : clientId,
            oId,
            recordId: rId,
            userId: clone ? appStore.userData.userId : userId,
            isPublic,
            isTemplate: clone ? false : !!isTemplate,
            productType,
            widgets: JSON.stringify({
                widgets: widgets.slice().map((w) => ({ ...w, data: undefined })),
                layouts,
                defaultDashSettings: clone
                    ? { oid: undefined, level: newName }
                    : R.pathOr(undefined, ['defaultDashSettings'], dashboardWidgetsField),
                templateSettings: dashboardWidgetsField.templateSettings,
                dashboardFilters: this.dashboardFilters,
                pinnedCustomTagFilters: this.pinnedCustomTagFilters,
            }),
        }
        let newDashboard
        try {
            if (value === 'new' || clone) {
                newDashboard = await appStore.http.post('/dashboard', JSON.stringify(body))
                appStore.addNotification(null, 'Dashboard Created', 'success')
            } else {
                newDashboard = await appStore.http.put(`/dashboard/${value}`, JSON.stringify(body))
                appStore.addNotification(null, 'Dashboard Updated', 'success')
            }
        } catch (e) {
            appStore.addNotification(null, `Error ${value === 'new' || clone ? 'Creating' : 'Updating'} Dashboard`, 'error')
        }
        await this.getDashboards(newDashboard.dashboardId)
        this.trackEditAction('saved', { is_new_dashboard: value === 'new' || clone })
        this.editing = false
        this.showTemplateCloneModal = false
    }

    setWidgetEditorKey = (widgetKey) => {
        this.widgetEditorKey = widgetKey
    }

    setWidgetEditorKeyNew = (widgetKey) => {
        this.widgetEditorKeyNew = widgetKey
    }

    updateWidget = (widgetIndex, widget) => {
        this.widgets[widgetIndex] = widget
    }

    updateLayouts = (newLayout) => {
        this.layouts = newLayout
    }

    toggleShowCreateDashboard = () => {
        console.log('inside', this.showCreateDashboard)
        this.showCreateDashboard = !this.showCreateDashboard
    }

    resetDashboardTabs = async (dashboards) => (
        Promise.all(dashboards.map(this.resetDashboardTab))
    )

    resetDashboardTab = async (dashboard) => {
        const { appStore } = this
        const { clientId } = appStore.userData

        const {
            name, oId, recordId, userId, isPublic, widgets, value: dashboardId, productType, isTemplate,
        } = dashboard

        const body = {
            dashboardId,
            name,
            clientId,
            oId,
            recordId,
            userId,
            isPublic,
            productType,
            isTemplate,
            widgets: JSON.stringify({
                ...widgets,
                defaultDashSettings: { oid: undefined, level: undefined },
            }),
        }
        return appStore.http.put(`/dashboard/${dashboardId}`, JSON.stringify(body))
    }

    dashboardIsTab = (dashboard) => (
        R.hasPath(['widgets', 'defaultDashSettings', 'level'], dashboard)
    )

    setDashboardTab = async ({ dashboard, name: tabName }) => {
        const { appStore } = this
        const { clientId } = appStore.userData
        const {
            name, oId, recordId, userId, isPublic, widgets, value: dashboardId, productType, isTemplate,
        } = dashboard

        const body = {
            dashboardId,
            name,
            clientId,
            oId,
            recordId,
            userId,
            isPublic,
            productType,
            isTemplate,
            widgets: JSON.stringify({
                ...widgets,
                defaultDashSettings: { oid: oId, level: tabName },
            }),
        }
        return appStore.http.put(`/dashboard/${dashboardId}`, JSON.stringify(body))
    }

    createScaledCanvas = (canvas, gridItem, width) => {
        const maxWidth = (width || gridItem.clientWidth - 30)
        const scale = maxWidth / canvas.width

        const scaledCanvas = document.createElement('canvas')
        const ctx = scaledCanvas.getContext('2d')

        scaledCanvas.width = maxWidth
        scaledCanvas.height = canvas.height * scale

        ctx.drawImage(
            canvas,
            0,
            0,
            canvas.width,
            canvas.height,
            0,
            0,
            scaledCanvas.width,
            scaledCanvas.height,
        )

        return scaledCanvas
    }

    exportIgnoreElements = (element) => {
        if (element.classList.contains('dynamicDashboard-header')) return true
        if (element.classList.contains('menu')) return true
        if (element.classList.contains('exploriDropdown')) return true
        if (element.classList.contains('barTooltip')) return true

        return false
    }

    resizeScrollableCanvas = (clone) => {
        Array.from(clone.getElementsByClassName('react-grid-item')).forEach((gridItem) => {
            Array.from(gridItem.getElementsByClassName('widgetContainer')).forEach((widget) => {
                widget.style.boxShadow = 'unset'
            })
            Array.from(gridItem.getElementsByClassName('scrollableX')).forEach((scrollable) => {
                try {
                    scrollable.classList.remove('scrollableX')
                    const canvas = scrollable.getElementsByTagName('canvas')[0]
                    const canvasParent = canvas.parentElement
                    const scaledCanvas = this.createScaledCanvas(canvas, gridItem)

                    canvasParent.removeChild(canvas)
                    canvasParent.appendChild(scaledCanvas)

                    /**
                     * This will resize the parent height to match new scaled canvas
                     */
                    // canvasParent.style.height = 'fit-content'
                    // gridItem.style.height = 'max-content'
                } catch (e) {
                    console.error(e)
                }
            })
        })
    }

    downloadAsPDF = async () => {
        const { appStore, dashboard } = this
        const { name } = dashboard
        try {
            const gridLayout = document.getElementsByClassName('react-grid-layout')[0]
            let {
                x: baseX,
                y: baseY,
            } = gridLayout.getBoundingClientRect()

            // eslint-disable-next-line no-undef, new-cap
            const pdf = new jsPDF({
                orientation: 'p',
                unit: 'px',
                format: 'a4',
            })

            const pdfWidth = pdf.internal.pageSize.getWidth()
            const pdfHeight = pdf.internal.pageSize.getHeight()
            let scale = 1
            if (pdfWidth < gridLayout.clientWidth) {
                scale = pdfWidth / gridLayout.clientWidth
            }

            const gridItems = Array.from(document.getElementsByClassName('react-grid-item'))
                .map((gridItem) => ({
                    gridItem,
                    ...gridItem.getBoundingClientRect().toJSON(),
                }))

            const sortedItems = R.sortWith([
                R.ascend(R.prop('y')),
                R.ascend(R.prop('x')),
            ], gridItems)

            // eslint-disable-next-line no-restricted-syntax
            for (const element of sortedItems) {
                const {
                    x,
                    y,
                    width,
                    height,
                    gridItem,
                } = element

                const offsetX = (x - baseX) * scale
                let offsetY = (y - baseY) * scale

                const options = {
                    scale: 1,
                    width,
                    height,
                    ignoreElements: this.exportIgnoreElements,
                    onclone: this.resizeScrollableCanvas,
                }

                // eslint-disable-next-line no-await-in-loop
                const canvas = await html2canvas(gridItem, options)

                if (offsetY + canvas.height * scale > pdfHeight) {
                    baseY = y - 10
                    offsetY = (y - baseY) * scale

                    pdf.addPage()
                }

                const src = canvas.toDataURL({ type: 'png', backgroundColor: '#FFF' })
                pdf.addImage(src, 'png', offsetX, offsetY, canvas.width * scale, canvas.height * scale, uuidv4())
            }

            pdf.save(`${name}.pdf`)
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error downloading PDF', 'error')
        }
    }

    downloadAsPNG = async () => {
        const { dashboard, appStore } = this
        const { name } = dashboard
        try {
            const options = {
                ignoreElements: this.exportIgnoreElements,
                onclone: (clone) => {
                    const title = clone.getElementById('dashboardTitle')
                    const container = clone.getElementById('dashboardContainer')
                    title.style.padding = '30px 0px 0px 10px'
                    title.style.display = 'block'
                    title.style.textAlign = 'center'
                    container.insertAdjacentElement('afterbegin', title)
                    this.resizeScrollableCanvas(clone)
                },
            }
            const canvas = await html2canvas(document.getElementById('dashboardContainer'), options)
            download(canvas.toDataURL(), name)
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error downloading PNG', 'error')
        }
    }

    toggleShowTemplateSelection = () => {
        this.showTemplateSelection = !this.showTemplateSelection
    }

    toggleShowCreateTemplateModal = () => {
        this.showCreateTemplateModal = !this.showCreateTemplateModal
    }

    toggleEditingTemplate = () => {
        this.editingTemplate = !this.editingTemplate
    }

    cloneDashboard = async (dashboardId) => {
        const {
            appStore, dashboards,
        } = this

        const dashboard = dashboards.find((d) => d.dashboardId === dashboardId)

        const {
            name,
        } = dashboard

        const body = {
            ...dashboard,
            isTemplate: false,
            dashboardId: 0,
            name,
            clientId: appStore.userData.clientId,
            userId: appStore.userData.userId,
            widgets: JSON.stringify(dashboard.widgets),
        }
        let newDashboard
        try {
            newDashboard = await appStore.http.post('/dashboard', JSON.stringify(body))
            appStore.addNotification(null, 'Dashboard Created', 'success')
        } catch (e) {
            appStore.addNotification(null, `Error ${'Creating'} Dashboard`, 'error')
        }
        await this.getDashboards(newDashboard.dashboardId)
        this.trackEditAction('saved', { is_new_dashboard: true })
        this.editing = false
    }

    toggleUseNewDashboardData = () => {
        this.useNewReport = !this.useNewReport
        this.init(true)
    }

    getFullSurveys = async (surveyIds) => (
        Promise.all(surveyIds.map((id) => this.appStore.http.get(`/survey/${id}`)))
    )

    getCampaignLists = async (surveyIds) => {
        const { appStore } = this
        const campaignLists = await Promise.all(
            surveyIds.map((id) => appStore.http.get(`/survey/${id}/getCampaignList`)),
        )

        return campaignLists.map((campaignList) => R.groupBy(R.prop('type'), campaignList.map((campaign) => ({
            id: campaign.campaignid,
            label: campaign.campaigntitle,
            type: campaign.campaignType,
        })))).flat()
    }

    getSurveyDataIdFilter = (filterGroup) => (
        Array.from(filterGroup.rules.reduce((acc, { options }) => {
            (options ?? []).forEach(({ surveyDataIds }) => {
                acc.add(surveyDataIds)
            })
            return acc
        }, new Set(this.urlSurveyDataIds))).filter((id) => !!id)
    )

    getQuestion = async (questionId, surveyId) => {
        const { appStore } = this
        const url = `/api/question/${questionId.toUpperCase()}/7`
        const question = await appStore.http.get(url)
        question.surveyId = surveyId
        question.questionId = questionId
        return question
    }

    mapResponses = (response) => (
        R.sortWith([
            R.descend(R.prop('numberOfResponses')),
            R.ascend(R.prop('label')),
        ], response.labels.map((label, index) => ({
            label,
            numberOfResponses: R.pathOr(0, ['numberOfResponses', index], response),
            surveyDataIds: R.pathOr([], ['surveyDataIds', index], response),
        })))
    )

    loadFullQuestions = async (surveys) => {
        await Promise.all(
            surveys.map(async ({ survey }) => {
                await Promise.all(
                    survey.pages.map(async (p, pI) => {
                        if (!p.questions) return
                        await Promise.all(
                            p.questions.map(async (q, qI) => {
                                const question = await this.getQuestion(q.questionId, survey.id)
                                survey.pages[pI].questions[qI] = { ...question, ...question.question, question: undefined }
                            }),
                        )
                    }),
                )
            }),
        )
    }

    getOpenEnded = async (surveys) => {
        const { appStore } = this
        const openEnded = new Map()

        const questions = surveys
            .map(({ survey }) => (
                survey.pages.map((p) => (
                    p.questions
                        ? p.questions
                        : []
                )))).flat(2)

        const filteredQuestions = questions.flat()
            .filter((q) => ['TA', 'TX', 'TL', 'UV', 'DT'].includes(q.questionTypeCode))

        await Promise.all(filteredQuestions.map(async (question) => {
            const {
                id, questionTypeCode, questionId, surveyId,
            } = question
            const request = JSON.stringify({
                questionId,
                questionType: questionTypeCode,
                questionOptionId: null,
                filter: {
                    state: ['completed'],
                    surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                    ...R.omit(['openEnded'], this.filters),
                },
            })

            const response = await appStore.http.post(`/report/v1/${surveyId}/textquestion`, request)

            const responses = ['TL', 'UV'].includes(questionTypeCode)
                ? Object.entries(response).reduce((map, [optionId, optionData]) => (
                    Object.assign(map, {
                        [optionId.toUpperCase()]: {
                            numberOfResponses: optionData.numberOfResponses.reduce((a, b) => a + b),
                            responses: this.mapResponses(optionData),
                        },
                    })
                ), {})
                : R.sortWith([
                    R.descend(R.prop('numberOfResponses')),
                    R.ascend(R.prop('label')),
                ], response.labels.map((label, index) => ({
                    label,
                    numberOfResponses: R.pathOr(0, ['numberOfResponses', index], response),
                    surveyDataIds: R.pathOr([], ['surveyDataIds', index], response),
                })))

            openEnded.set(id, responses)
        }))
        return openEnded
    }

    saveFilter = (filterKey, ruleGroup) => {
        this.filters[filterKey] = RuleGroup.clone(ruleGroup)
        this.showFilters = false
        this.trackFilterModal('saved', 'question')
        this.loading = true
        const hasRules = this.filters.question.hasRules()
        || this.filters.campaign.hasRules()
        || this.filters.responseDate.hasRules()
        || this.filters.openEnded.hasRules()
        || ruleGroup.hasRules()
        || this.filterStore.pinnedFilterQuestion

        if (!hasRules) this.filterRespondentType = undefined
        this.init(true)
    }

    toggleShowFilters = () => {
        if (this.showSelectFilterRespondent) {
            this.showSelectFilterRespondent = false
            if (!this.filterRespondentType) return
        }
        if (!this.filterRespondentType) {
            this.showSelectFilterRespondent = true
            return
        }
        const opening = !this.showFilters
        this.showFilters = opening
        this.trackFilterModal(opening ? 'opened' : 'closed', 'question')
    }

    closeFilters = () => {
        this.showFilters = false
        this.trackFilterModal('closed', 'question')
    }

    closeFilterRespondentTypeModal = () => {
        this.filterRespondentType = undefined
        this.showSelectFilterRespondent = false
    }

    loadAllOpenEnded = async () => {
        const openEnded = await this.getOpenEnded(this.filterSurveys)
        this.openEnded = openEnded
    }

    loadFilterData = async () => {
        const filterSurvey = this.filterSurveys
            .filter(({ survey }) => survey.metadata.taxonomy.respondentType === this.filterRespondentType)
        this.filterStore.reportPages = filterSurvey
            .map((fs) => R.pathOr([], ['survey', 'pages'], fs)).flat().filter((p) => !!p.questions)

        const [campaignLists, openEnded] = await Promise.all([
            this.getCampaignLists(filterSurvey.map(({ survey }) => survey.id)),
            this.getOpenEnded(filterSurvey),
        ])
        this.filterStore.campaignList = campaignLists[0]
        this.filterStore.openEnded = openEnded
    }

    setFilterRespondent = ({ value }) => {
        this.filterRespondentType = value
    }

    getPinnedFilterValue = () => {
        if (!this.filterStore.pinnedFilterQuestion) return []

        return [...this.filterStore.pinnedRuleGroup.existingIds]
    }

    editPinnedFilter = ({ value }) => {
        const { filterStore } = this
        const {
            pinnedRuleGroup,
            addFilterRule,
            pinnedQuestionLabel,
            pinnedFilterQuestion,
            pinnedQuestionTypeCode,
            pinnedIsOpenEnded,
        } = filterStore
        pinnedRuleGroup.discard()
        value.forEach((v) => {
            addFilterRule(pinnedRuleGroup, pinnedQuestionLabel, pinnedFilterQuestion, v, pinnedQuestionTypeCode)
        })
        this.saveFilter(pinnedIsOpenEnded ? 'openEnded' : 'question', pinnedRuleGroup)
        this.trackPinnedFilterChanged('pinned_question', pinnedQuestionLabel, value.length)
    }

    toggleShowWidgetCreator = () => {
        if (this.showWidgetCreator) {
            this.widgetEditorKeyNew = undefined
        }
        this.showWidgetCreator = !this.showWidgetCreator
    }

    toggleShowAddPinnedFilters = () => {
        const opening = !this.showAddPinnedFilter
        this.showAddPinnedFilter = opening
        this.trackFilterModal(opening ? 'opened' : 'closed', 'pinned_question')
    }

    addDashboardFilters = (filter) => {
        this.dashboardFilters.push(filter)
        this.trackFilterModal('saved', 'pinned_question')
    }

    removeDashboardFilters = (index) => {
        this.dashboardFilters = this.dashboardFilters.filter((f, i) => i !== index)
        this.updateFilterChangeKey()
        this.init(true)
    }

    getPageOptions = () => {
        const { appStore } = this
        const { langcode } = appStore
        if (!this.filterSurveys) return []
        const allQuestions = this.filterSurveys
            .map((s) => s.survey.pages)
            .flat(2)
            .filter((p) => !!p.questions)
            .map((page) => {
                const { questions } = page
                const pageQuestions = questions
                    .filter(({ questionTypeCode }) => !['ST', 'TA', 'TX', 'TL', 'UV', 'I', 'OD', 'DT', 'FM', 'NF'].includes(questionTypeCode))
                    .map((question) => {
                        const { questionTypeCode, metadata } = question
                        const { initialLocale } = metadata

                        const questionTitleObj = getQuestionTitle({
                            content: question.content.shortTitle,
                            formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                            locale: langcode,
                            defaultLocale: langcode,
                            initialLocale,
                        })
                        const questionTitle = R.pathOr(questionTitleObj, ['text'], questionTitleObj)

                        let options = (question.optionSet || question.optionSetY).options
                            .map((o) => {
                                const label = getOptionTitle({
                                    langs: o.langs,
                                    locale: langcode,
                                    defaultLocale: langcode,
                                    initialLocale,
                                })
                                return {
                                    id: `${question.id}_${o.id}`,
                                    label: R.pathOr(label, ['text'], label),
                                    setYId: o.id,
                                }
                            })

                        if (['SG', 'TB', 'RT'].includes(questionTypeCode)) {
                            const setXOptions = (question.optionSetX || { options: [] }).options
                                .map((o) => {
                                    const label = getOptionTitle({
                                        langs: o.langs,
                                        locale: langcode,
                                        defaultLocale: langcode,
                                        initialLocale,
                                    })
                                    return {
                                        label: R.pathOr(label, ['text'], label),
                                        setXId: o.id,
                                    }
                                })

                            options = options.reduce((list, { label, setYId }) => (
                                list.concat(
                                    setXOptions.reduce((listX, optionX) => {
                                        listX.push({
                                            id: `${question.id}_${setYId}_${optionX.setXId}`,
                                            label: `${label} - ${optionX.label}`,
                                            setYId,
                                            setXId: optionX.setXId,
                                        })
                                        return listX
                                    }, []),
                                )
                            ), [])
                        }
                        return {
                            label: questionTitle,
                            id: question.id,
                            questionTypeCode,
                            options,
                        }
                    })

                return pageQuestions
            })

        return allQuestions.flat(2)
    }

    refreshDashboardFilters = () => {
        const { addFilterRule } = this.filterStore
        const filterRuleObj = this.filterRespondentType
            ? {
                [this.filterRespondentType]: {
                    question: this.filters.question, openEnded: this.filters.openEnded,
                },
            }
            : {}
        this.dashboardFilters.forEach(({
            question,
            answerOptionQuestionId,
            filterLabel,
            options,
            respondentType,
        }) => {
            filterRuleObj[respondentType] = filterRuleObj[respondentType]
                || { question: undefined, openEnded: undefined }

            const questionId = answerOptionQuestionId || question
            const questionTypeCode = this.questionTypes[questionId]

            const isOpenEnded = (
                ['TA', 'TX', 'TL', 'UV', 'DT'].includes(questionTypeCode)
                || ['VMAT', 'heat'].includes(question)
            )
            const ruleType = isOpenEnded ? 'openEnded' : 'question'

            const ruleGroup = filterRuleObj[respondentType][ruleType]
                || new RuleGroup(RuleGroup.getDefaultLogic('question'))

            if (options && options.length) {
                options.forEach((o) => {
                    addFilterRule(ruleGroup, filterLabel, questionId, o, questionTypeCode)
                })
            }
            filterRuleObj[respondentType][ruleType] = ruleGroup
        })

        this.dashboardFilterRuleGroups = filterRuleObj
        // this.init(true)
    }

    editDashboardFilterOptions = (options, index, type) => {
        const newOptions = type === 'single' ? [options].filter((o) => !!o.value) : options.value
        const { filterLabel } = this.dashboardFilters[index]
        this.dashboardFilters[index].options = newOptions
        this.trackPinnedFilterChanged('dashboard_question', filterLabel, newOptions.length)

        console.log('editDashboardFilterOptions', index, type, options, this.dashboardFilters[index].question)
        if (['VMAT', 'heat'].includes(this.dashboardFilters[index].question) && !options.value) {
            this.removeDashboardFilters(index)
            return
        }

        this.dashboardFilters[index].options = type === 'single' ? [options].filter((o) => !!o.value) : options.value
    }

    setBreadcrumbs = () => {
        if (!this?.oId) return []
        const scope = {
            3: {
                title: i18n.t('company'),
                path: '/',
            },
            4: {
                title: i18n.t('division'),
                path: `/division/?divisionId=${this.recordId}`,
            },
            5: {
                title: i18n.t('portfolio'),
                path: `/portfolio/?portfolioId=${this.recordId}`,
            },
            6: {
                title: i18n.t('event'),
                path: `/event/?productId=${this.recordId}`,
            },
        }[this.oId]
        const breadcrumbDetails = [{
            name: 'dashboard',
            url: '/company/dashboard',
            title: i18n.t('dashboards'),
        }, {
            name: 'dashBoardScope',
            url: scope.path,
            title: scope.title,
        }]
        if (this.oId !== 3) {
            breadcrumbDetails.push(
                {
                    name: 'dashBoardScopeName',
                    url: scope.path,
                    title: this.scopeName,
                },
            )
        }
        this.appStore.breadcrumps = breadcrumbDetails
    }

    toggleShowAddPinnedCustomTagFilters = () => {
        const opening = !this.showAddPinnedCustomTagFilter
        this.showAddPinnedCustomTagFilter = opening
        this.trackFilterModal(opening ? 'opened' : 'closed', 'pinned_custom_tag')
    }

    addPinnedCustomtagFilter = (customTagFilter) => {
        this.pinnedCustomTagFilters.push({ ...customTagFilter, key: uuidv4() })
        this.trackFilterModal('saved', 'pinned_custom_tag')
    }

    editPinnedCustomTagfilter = (value, tagIndex) => {
        const { filterLabel } = this.pinnedCustomTagFilters[tagIndex]
        this.pinnedCustomTagFilters[tagIndex].options = value
        this.trackPinnedFilterChanged('custom_tag', filterLabel, value.length)
    }

    removeDashboardCustomTagFilters = (index) => {
        this.pinnedCustomTagFilters = this.pinnedCustomTagFilters.filter((_, i) => i !== index)
        this.init(true)
    }

    updateFilterChangeKey = () => {
        this.filterChangeKey = uuidv4()
    }
}

export default DynamicDashboardStore
